import { useEffect, useState } from "react";

/**
 * Perf helper: returns `true` only after the browser is idle (or after a short
 * timeout). Use it to gate non-critical background queries (unread badges, XP,
 * subscription checks) so they don't compete with the data the user is actually
 * looking at — especially on mobile, where the browser only allows a handful of
 * parallel requests.
 */
export function useIdle(timeout = 1200) {
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (idle) return;
    let cancelled = false;
    const done = () => {
      if (!cancelled) setIdle(true);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(done, { timeout });
      return () => {
        cancelled = true;
        w.cancelIdleCallback?.(id);
      };
    }
    const t = window.setTimeout(done, timeout);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [idle, timeout]);

  return idle;
}
